// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import compose from 'recompose/compose';
import { connect } from 'react-redux';
import { shape, func, string, bool, arrayOf, object } from 'prop-types';
import {
  Grid,
  withStyles,
} from '@material-ui/core';
import { capitalize, get, upperCase } from 'lodash';

import { getI18n } from '../../../../utils/intl';
import {
  Footer,
  HelpContainer,
  FormTextField,
  FormActionButtons,
  FormSelect,
} from '../../../../components';
import {
  fetchProduct,
  saveProducts,
  updateProducts,
} from '../../../../actions/products';

import {
  getIsProductSaveSuccess,
} from '../../../../reducers/products';
import { getEulas } from '../../../../reducers/api';
import { PRODUCTS_ADD_HELP_ITEMS, ALERT_SUCCESS } from '../../../../constants';
import styles from './styles';


export const Overview = (props) => {
  const {
    classes,
    productName,
    accessStatus,
    productDetails,
    isProductSaveSuccess,
    productUuid,
    isFieldEditName,
    notifyMessages,
    onHideEditTitle,
    resetAccessStatus,
    isCurrentTab,
    hasUpdate,
    eulas,
    isProductAssignedToAnyApp,
  } = props;

  const intl = getI18n(useIntl());
  const [name, setName] = useState(productName);
  const [description, setDescription] = useState('');
  const [eulaUuid, setEulaUuid] = useState(null);
  const [isFieldEdit, setIsFieldEdit] = useState(isFieldEditName);
  const [eulasSelectOptions, setEulaSelectOptions] = useState([]);

  const onClearDescription = () => {
    setDescription(get(productDetails, 'description'));
  }

  useEffect(() => {
    if(productDetails) {
      setName(productDetails.name);
      setDescription(productDetails.description);
      if(productDetails.eulaUuid){
        setEulaUuid(productDetails.eulaUuid);
      }else{
        setEulaUuid(null);
      }
    }
  }, [productDetails]);

  useEffect(() => {
    if(productDetails && accessStatus !== capitalize(productDetails.accessStatus)) {
      setIsFieldEdit(true);
    } else {
      setIsFieldEdit(isFieldEditName);
    }
  }, [accessStatus])

  useEffect(()=> {
    setIsFieldEdit(isFieldEditName);
  }, [isFieldEditName]);

  useEffect(()=> {
    setName(productName);
  }, [productName]);

  useEffect(() => {
    if(isCurrentTab){
      onClearDescription();
    }
  }, [isCurrentTab]);

  useEffect(() => {
    setEulaSelectOptions(eulas);
  }, [eulas])


  useEffect(() => {
    if (isProductSaveSuccess) {
      localStorage.getItem('page') === 'add' ?
        notifyMessages(ALERT_SUCCESS, intl.getI18nMessage('label.product.created'))
      : notifyMessages(ALERT_SUCCESS, intl.getI18nMessage('label.product.updated'));
      props.push(`/admin/products/details/${productDetails.uuid}`);
    }
  }, [isProductSaveSuccess, productDetails]);

  const handleChange = (value) => {
    localStorage.setItem('isUnSavedChanges-products', true);
    setIsFieldEdit(true);
    setDescription(value);
  }

  const handleChangeEula = (value) => {
    localStorage.setItem('isUnSavedChanges-products', true);
    setIsFieldEdit(true);
    setEulaUuid(value === "0" ? null : value);
  }

  const onNext = () => {
    if(productUuid) {
      localStorage.setItem('page', 'edit');
      props.updateProducts(name, description, productUuid, eulaUuid, upperCase(accessStatus));
    } else {
      localStorage.setItem('page', 'add');
      props.saveProducts(name, description, eulaUuid, upperCase(accessStatus));
    }
    localStorage.setItem('isUnSavedChanges-products', false);
    setIsFieldEdit(false);
  }

  const onCancel = () => {
    localStorage.setItem('isUnSavedChanges-products', false);
    if(productUuid) {
      onHideEditTitle();
      resetAccessStatus();
      onClearDescription();
      setIsFieldEdit(false);
      setDescription(productDetails.description);
      if(productDetails.eulaUuid){
        setEulaUuid(productDetails.eulaUuid);
      }else{
        setEulaUuid(null);
      }
    } else {
      props.push('/admin/products');
    }
  }

  return (
    <div
      id="products-details-overview-container"
      data-apim-test="products-details-overview-container"
      data-layer7-test="products-details-overview-container"
    >
      <Grid container className={classes.marginPanelContainer}>
        <Grid item md={6} sm={12} xs={12} className={classes.paper}>
          <h4 className={classes.title}>{intl.getI18nMessage('label.details')}</h4>
          <FormTextField
            id={'product-details-page-description'}
            name={intl.getI18nMessage('label.description')}
            value={description}
            handleChange={(value) => handleChange(value)}
            helperText={intl.getI18nMessage(
              'label.description.help',
            )}
            multiline
            rows={4}
            optional
            maxLength={'255'}
            disabled={!hasUpdate}
          />
          <FormSelect
            id={'product-details-page-eula'}
            name={intl.getI18nMessage('label.eula')}
            helperText={intl.getI18nMessage('label.product.overview.eula.helptext')}
            value={eulaUuid === null ? "0" : eulaUuid}
            data={eulasSelectOptions}
            handleChange={handleChangeEula}
            defaultText={"Select EULA"}
            optional
            noNativeSelect
            disabled={!hasUpdate || isProductAssignedToAnyApp}
            fieldContainerClass={classes.eulaFieldContainer}
          />
        </Grid>
        <Grid item md={6} sm={12} xs={12} className={classes.paper}>
          <HelpContainer
            id="product-details-overview-help-container"
            helpItems={PRODUCTS_ADD_HELP_ITEMS}
            titleClass={classes.title}
          />
        </Grid>
        <FormActionButtons
          isContainerFooter={true}
          onNextClick={onNext}
          onCancelClick={onCancel}
          nextText={intl.getI18nMessage('label.save.button')}
          cancelText={intl.getI18nMessage('label.cancel.button')}
          id="product-details-form-buttons"
          containerClass={!isFieldEdit?
            classes.hideFooterContainer: classes.showFooterContainer}
        />
      </Grid>
      <Footer />
    </div>
  );
};

Overview.propTypes = {
  classes: shape({}),
  productName: string,
  productDetails: shape({}),
  isProductSaveSuccess: bool,
  productUuid: string,
  isFieldEditName: bool,
  isCurrentTab: bool,
  notifyMessages: func,
  push: func,
  updateProducts: func,
  saveProducts: func,
  onHideEditTitle: func,
  hasUpdate: bool,
  eulas: arrayOf(object),
  accessStatus : string,
  resetAccessStatus : func,
  isProductAssignedToAnyApp : bool,
};

const mapStateToProps = state => ({
  isProductSaveSuccess: getIsProductSaveSuccess(state),
  eulas: getEulas(state),
});

const mapDispatchToProps = {
  saveProducts,
  updateProducts,
  fetchProduct,
};

Overview.displayName = 'Overview';

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(Overview);

